import { Trophy } from "lucide-react";
import type { AdsActivityResponse } from "@/lib/adsAssistantClient";
import { AchievementBadge, KpiStatusBadge, StreakChip, useFreshAchievementToasts } from "@/components/common/kpi";
import type { FreshAchievement, KpiTone } from "@/components/common/kpi";
import { KPI_TONE_CLASSES } from "@/components/common/kpi/kpiTypes";
import { AdsActivityCardCta } from "@/components/ads-assistant/AdsActivityCardCta";
import { ADS_INDEXED_ANCHOR_ID, ADS_MANUAL_FORM_ID } from "@/components/ads-assistant/adsActivityCardIds";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

function ArrowPathIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function isKpiTone(v: string): v is KpiTone {
  return v === "amber" || v === "green" || v === "gold" || v === "red" || v === "grey";
}

export function AdsActivityCard(props: {
  activity: AdsActivityResponse | null;
  refreshing: boolean;
  onRefresh: () => void;
  hasManualForm: boolean;
  canOpenLibrary: boolean;
  onOpenLibraryTab?: () => void;
  onOpenIngestionTab?: () => void;
  onBeforeScrollToIndexedPosts?: () => void;
  onOpenAchievements?: () => void;
}) {
  const {
    activity,
    refreshing,
    onRefresh,
    hasManualForm,
    canOpenLibrary,
    onOpenLibraryTab,
    onOpenIngestionTab,
    onBeforeScrollToIndexedPosts,
    onOpenAchievements,
  } = props;

  const verdict = activity?.status_verdict ?? null;
  const coachingOffNoGoal = verdict?.label === "Coaching off";
  const coachingOn = verdict != null && !coachingOffNoGoal;
  const tone: KpiTone | null =
    verdict && !coachingOffNoGoal && isKpiTone(verdict.tone) ? verdict.tone : null;
  const ringCls = tone ? KPI_TONE_CLASSES[tone].ring : "";

  const myToday = activity?.my_posts_today;
  const goal = activity?.ads_daily_goal;
  const cap = activity?.daily_max_per_platform_total;
  const streak = activity?.streak_days ?? 0;
  const rolling = activity?.my_rolling_avg_per_day;
  const orgToday = activity?.posts_today_count;
  const windowCount = activity?.posts_in_feed_window_count;
  const windowDays = activity?.feed_retention_days;

  const freshList = (activity?.fresh_achievements ?? []) as FreshAchievement[];
  const { freshById, liveMessage } = useFreshAchievementToasts(freshList.length ? freshList : null);

  const showAboveBeyond =
    verdict?.tone === "gold" &&
    typeof myToday === "number" &&
    typeof goal === "number" &&
    goal > 0 &&
    myToday > goal;

  const orgRepCount = activity?.org_active_rep_count;
  const orgAvg = activity?.org_avg_per_rep_today;
  const showOrgPace =
    activity?.use_in_org_activity !== false &&
    typeof orgRepCount === "number" &&
    orgRepCount > 0 &&
    typeof orgAvg === "number";

  return (
    <div
      {...{ [FIREFLY_TARGET_ATTR]: "ads-activity-coaching" }}
      className={[
        "rounded-lg border bg-gray-50/90 px-3 py-2 dark:bg-zinc-800/50",
        coachingOn && tone
          ? ["ring-2", ringCls, "border-transparent"].join(" ")
          : "border-gray-200 dark:border-gray-600",
      ].join(" ")}
    >
      <span className="sr-only" aria-live="polite">
        {liveMessage}
      </span>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Org activity</p>
        <div className="flex flex-wrap items-center gap-2">
          {coachingOn && verdict && tone ? (
            <>
              <KpiStatusBadge tone={tone} label={verdict.label} compact />
              <StreakChip days={streak} compact />
            </>
          ) : verdict && coachingOffNoGoal ? (
            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Coaching off</span>
          ) : null}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh org activity"
            title="Refresh"
            className="-mr-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200/70 hover:text-gray-800 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-zinc-700 dark:hover:text-gray-100"
          >
            <ArrowPathIcon className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")} />
          </button>
        </div>
      </div>

      {verdict ? <p className="mt-1.5 text-xs text-gray-700 dark:text-gray-200">{verdict.message}</p> : null}

      {showAboveBeyond ? (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-kpi-gold/35 bg-kpi-gold/10 px-2 py-1.5 dark:border-kpi-gold-dark/40 dark:bg-kpi-gold-dark/15">
          <Trophy className="mt-0.5 h-4 w-4 shrink-0 text-yellow-700 dark:text-yellow-200" aria-hidden />
          <p className="text-[11px] font-medium text-yellow-950 dark:text-yellow-100">
            Above and beyond — you are ahead of today&apos;s goal. Keep quality and cadence, not just volume.
          </p>
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-800 dark:text-gray-100">
        {coachingOn && typeof myToday === "number" && typeof goal === "number" && goal > 0 ? (
          <span className="tabular-nums">
            <span className="font-semibold text-gray-900 dark:text-gray-50">
              {myToday}
              <span className="font-normal text-gray-500 dark:text-gray-400">/{goal}</span>
            </span>
            <span className="ml-1 text-xs font-normal text-gray-600 dark:text-gray-400">you today (goal)</span>
          </span>
        ) : typeof myToday === "number" ? (
          <span className="tabular-nums">
            <span className="font-semibold text-gray-900 dark:text-gray-50">{myToday}</span>
            <span className="ml-1 text-xs font-normal text-gray-600 dark:text-gray-400">you today</span>
          </span>
        ) : (
          <span className="text-xs text-gray-500">You today: —</span>
        )}
        <span className="tabular-nums">
          <span className="font-semibold text-gray-900 dark:text-gray-50">
            {orgToday ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
          </span>
          <span className="ml-1 text-xs font-normal text-gray-600 dark:text-gray-400">org today</span>
        </span>
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {windowCount ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
          </span>
          <span className="ml-1 text-xs font-normal text-gray-600 dark:text-gray-400">in last {windowDays ?? "—"}d</span>
        </span>
      </div>

      {typeof rolling === "number" && coachingOn ? (
        <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">
          Your 14-day pace: <span className="tabular-nums font-medium text-gray-800 dark:text-gray-200">{rolling}</span>{" "}
          ads/day avg
        </p>
      ) : null}

      {typeof cap === "number" && cap > 0 && coachingOn ? (
        <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-500">Daily soft cap (org): {cap} ads</p>
      ) : null}

      <AdsActivityCardCta
        verdict={verdict}
        canOpenLibrary={canOpenLibrary}
        hasManualForm={hasManualForm}
        manualFormId={ADS_MANUAL_FORM_ID}
        indexedAnchorId={ADS_INDEXED_ANCHOR_ID}
        onOpenLibraryTab={onOpenLibraryTab}
        onOpenIngestionTab={onOpenIngestionTab}
        onBeforeScrollToIndexedPosts={onBeforeScrollToIndexedPosts}
      />

      {freshList.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5" aria-label="New achievements">
          {freshList.map((a) => (
            <AchievementBadge
              key={a.id}
              achievementKey={a.achievement_key}
              metadata={a.metadata}
              fresh={freshById.has(a.id)}
            />
          ))}
        </div>
      ) : null}

      {onOpenAchievements ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={onOpenAchievements}
            className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
          >
            View achievements
          </button>
        </div>
      ) : null}

      {activity?.show_recent_logger_names && activity.recent_loggers.length ? (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          Recent loggers:{" "}
          {activity.recent_loggers.slice(0, 6).map((l, i) => (
            <span key={l.user_id + l.published_at}>
              {i > 0 ? " · " : ""}
              <span className="font-medium text-gray-800 dark:text-gray-200">{l.display_name}</span>
            </span>
          ))}
        </p>
      ) : null}

      {showOrgPace ? (
        <p className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-500">
          Org pace today:{" "}
          <span className="tabular-nums font-medium text-gray-700 dark:text-gray-300">{orgAvg!.toFixed(1)}</span>
          /rep across {orgRepCount} active rep{orgRepCount === 1 ? "" : "s"}.
        </p>
      ) : null}
    </div>
  );
}
